/**
 * Registration Request Form - Extensible Validation System
 * 
 * This module provides client-side validation for the registration request contact form.
 * It's designed to be easily extensible for adding custom fields in the future.
 * 
 * Note: This form collects registration requests (Name, Surname, Email).
 * Backend logic for saving and processing will be implemented separately.
 */

// Configuration object for all form field validations
const FIELD_VALIDATORS = {
  'name': {
    required: true,
    minLength: 2,
    pattern: /^[a-zA-Z\s'\-]+$/,
    errorMessages: {
      required: 'Name is required',
      minLength: 'Name must be at least 2 characters',
      pattern: 'Please enter a valid name (letters, spaces, hyphens, and apostrophes only)'
    }
  },
  'surname': {
    required: true,
    minLength: 2,
    pattern: /^[a-zA-Z\s'\-]+$/,
    errorMessages: {
      required: 'Surname is required',
      minLength: 'Surname must be at least 2 characters',
      pattern: 'Please enter a valid surname (letters, spaces, hyphens, and apostrophes only)'
    }
  },
  'email': {
    required: true,
    pattern: /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i,
    errorMessages: {
      required: 'Email is required',
      pattern: 'Please enter a valid email address'
    }
  },
  'company': {
    required: true,
    minLength: 2,
    errorMessages: {
      required: 'Company Name is required',
      minLength: 'Company Name must be at least 2 characters'
    }
  },
  'company_id': {
    required: false,
    errorMessages: {}
  }
  // Future custom fields can be added here
  // Example:
  // 'contact[Phone]': {
  //   required: false,
  //   pattern: /^[0-9]{10}$/,
  //   errorMessages: {
  //     pattern: 'Please enter a valid 10-digit phone number'
  //   }
  // }
};

/**
 * Validates a single field based on its configuration
 * @param {string} fieldName - The name attribute of the field
 * @param {string} value - The value to validate
 * @returns {Object} - { valid: boolean, message: string }
 */
function validateField(fieldName, value) {
  const validator = FIELD_VALIDATORS[fieldName];

  // If no validator is configured, consider it valid
  if (!validator) {
    return { valid: true, message: '' };
  }

  // Check required validation
  if (validator.required && !value.trim()) {
    return {
      valid: false,
      message: validator.errorMessages.required
    };
  }

  // Skip further validation if field is empty and not required
  if (!value.trim() && !validator.required) {
    return { valid: true, message: '' };
  }

  // Check minimum length validation
  if (validator.minLength && value.length < validator.minLength) {
    return {
      valid: false,
      message: validator.errorMessages.minLength
    };
  }

  // Check pattern validation
  if (validator.pattern && !validator.pattern.test(value)) {
    return {
      valid: false,
      message: validator.errorMessages.pattern
    };
  }

  return { valid: true, message: '' };
}

/**
 * Displays an error message for a specific field
 * @param {string} fieldId - The ID of the input field
 * @param {string} message - The error message to display
 */
function showFieldError(fieldId, message) {
  const field = document.getElementById(fieldId);
  if (!field) return;

  const errorSpan = document.querySelector(`[data-error-for="${fieldId}"]`);

  if (errorSpan) {
    errorSpan.textContent = message;
    errorSpan.style.display = 'block';
  }

  field.classList.add('field-error-state');
  field.setAttribute('aria-invalid', 'true');
}

/**
 * Clears the error message for a specific field
 * @param {string} fieldId - The ID of the input field
 */
function clearFieldError(fieldId) {
  const field = document.getElementById(fieldId);
  if (!field) return;

  const errorSpan = document.querySelector(`[data-error-for="${fieldId}"]`);

  if (errorSpan) {
    errorSpan.textContent = '';
    errorSpan.style.display = 'none';
  }

  field.classList.remove('field-error-state');
  field.removeAttribute('aria-invalid');
}

/**
 * Real-time validation for individual fields
 * @param {HTMLInputElement} field - The input field to validate
 */
function validateFieldRealtime(field) {
  const fieldName = field.getAttribute('name');
  const fieldId = field.id;
  const value = field.value;

  if (!FIELD_VALIDATORS[fieldName]) return;

  const result = validateField(fieldName, value);

  if (!result.valid && value.length > 0) {
    // Only show error if user has started typing
    showFieldError(fieldId, result.message);
  } else if (result.valid) {
    clearFieldError(fieldId);
  }
}

/**
 * Validates all fields in the form
 * @param {HTMLFormElement} form - The form element
 * @returns {boolean} - True if all fields are valid
 */
function validateAllFields(form) {
  let isValid = true;
  // Get all unique field names that have validators
  const fieldNames = new Set(Object.keys(FIELD_VALIDATORS));

  fieldNames.forEach(fieldName => {
    const inputs = form.querySelectorAll(`input[name="${fieldName}"]`);

    // For repeatable fields like 'company', validate each instance
    inputs.forEach(input => {
      const result = validateField(fieldName, input.value);
      if (!result.valid) {
        // Only show error for visible inputs
        if (input.type !== 'hidden') {
          showFieldError(input.id, result.message);
          isValid = false;
        }
      } else {
        if (input.type !== 'hidden') {
          clearFieldError(input.id);
        }
      }
    });
  });

  return isValid;
}

/**
 * Initialize the registration form validation and dynamic clinics
 */
function initRegistrationForm() {
  const form = document.getElementById('ContactForm');
  const clinicsList = document.getElementById('ClinicsList');
  const addClinicBtn = document.getElementById('AddClinic');
  const hiddenClinics = document.getElementById('HiddenClinics');
  const companyInput = document.getElementById('ContactForm-company');
  const companyIdInput = document.getElementById('ContactForm-company-id');

  if (!form) return;

  let pillIdCounter = 0;

  // Handle adding new clinic as a pill
  if (addClinicBtn && clinicsList && hiddenClinics && companyInput) {
    addClinicBtn.addEventListener('click', function () {
      const companyVal = companyInput.value.trim();
      const companyIdVal = companyIdInput.value.trim();

      // Basic validation for company name before adding
      if (companyVal.length < 2) {
        showFieldError(companyInput.id, FIELD_VALIDATORS.company.errorMessages.minLength);
        companyInput.focus();
        return;
      }

      const id = `pill-${pillIdCounter++}`;

      // Create Pill UI
      const pill = document.createElement('div');
      pill.className = 'clinic-pill';
      pill.dataset.pillId = id;
      pill.innerHTML = `
        <span class="clinic-pill__name">${companyVal}</span>
        ${companyIdVal ? `<span class="clinic-pill__id">(${companyIdVal})</span>` : ''}
        <button type="button" class="clinic-pill__remove" aria-label="Remove">&times;</button>
      `;

      // Create Hidden Inputs for form submission
      const hiddenNameInput = document.createElement('input');
      hiddenNameInput.type = 'hidden';
      hiddenNameInput.name = 'company';
      hiddenNameInput.value = companyVal;
      hiddenNameInput.dataset.linkedPill = id;

      const hiddenIdInput = document.createElement('input');
      hiddenIdInput.type = 'hidden';
      hiddenIdInput.name = 'company_id';
      hiddenIdInput.value = companyIdVal;
      hiddenIdInput.dataset.linkedPill = id;

      clinicsList.appendChild(pill);
      hiddenClinics.appendChild(hiddenNameInput);
      hiddenClinics.appendChild(hiddenIdInput);

      // Clear visible inputs and reset focus
      companyInput.value = '';
      companyIdInput.value = '';
      clearFieldError(companyInput.id);
      companyInput.focus();
    });

    // Handle pill removal
    clinicsList.addEventListener('click', function (e) {
      if (e.target.closest('.clinic-pill__remove')) {
        const pill = e.target.closest('.clinic-pill');
        const id = pill.dataset.pillId;

        pill.style.opacity = '0';
        pill.style.transform = 'scale(0.95)';

        setTimeout(() => {
          pill.remove();
          // Remove linked hidden inputs
          const linkedInputs = hiddenClinics.querySelectorAll(`[data-linked-pill="${id}"]`);
          linkedInputs.forEach(input => input.remove());
        }, 200);
      }
    });
  }

  /**
   * Submits the form via AJAX and handles the response
   * @param {HTMLFormElement} form 
   */
  async function submitForm(form) {
    const submitButton = form.querySelector('button[type="submit"]');
    const successMessage = document.getElementById('form-success');
    const errorMessage = document.getElementById('form-error');
    const fieldsContainer = form.querySelector('.contact__fields');
    const clinicsContainer = form.querySelector('.clinics-utility');
    const buttonContainer = form.querySelector('.contact__button');

    const contactContainer = form.closest('.contact');

    // Disable button and show loading state
    if (submitButton) {
      submitButton.disabled = true;
      if (contactContainer) contactContainer.classList.add('form-loading');
    }

    // Hide any previous messages
    if (successMessage) successMessage.style.display = 'none';
    if (errorMessage) errorMessage.style.display = 'none';

    try {
      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Show success message
        if (successMessage) {
          successMessage.style.display = 'flex';
          successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          successMessage.focus();
        }

        // Hide form fields to prevent double submission and clear the UI
        if (fieldsContainer) fieldsContainer.style.display = 'none';
        if (clinicsContainer) clinicsContainer.style.display = 'none';
        if (buttonContainer) buttonContainer.style.display = 'none';

        // Hide title if it exists
        const title = form.closest('.contact')?.querySelector('.title');
        if (title) title.style.display = 'none';

      } else {
        // Show error message from backend
        if (errorMessage) {
          const errorHeading = errorMessage.querySelector('h2');
          if (errorHeading && result.message) {
            const icon = errorHeading.querySelector('svg');
            errorHeading.innerHTML = '';
            if (icon) errorHeading.appendChild(icon);
            errorHeading.appendChild(document.createTextNode(' ' + result.message));
          }
          errorMessage.style.display = 'block';
          errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        if (contactContainer) contactContainer.classList.remove('form-loading');
        if (submitButton) {
          submitButton.disabled = false;
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
      if (errorMessage) {
        errorMessage.style.display = 'block';
        errorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (contactContainer) contactContainer.classList.remove('form-loading');
      if (submitButton) {
        submitButton.disabled = false;
      }
    }
  }

  // Handle form submission
  form.addEventListener('submit', function (event) {
    event.preventDefault();

    const isValid = validateAllFields(form);

    if (isValid) {
      submitForm(form);
    } else {
      const firstError = form.querySelector('.field-error-state');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstError.focus();
      }
    }
  });

  // Initial field setup for real-time validation
  form.querySelectorAll('input:not([type="hidden"])').forEach(field => {
    field.addEventListener('blur', function () {
      validateFieldRealtime(this);
    });

    field.addEventListener('input', function () {
      if (this.classList.contains('field-error-state')) {
        clearFieldError(this.id);
      }
    });
  });

  console.log('Registration form with pilled clinics initialized');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRegistrationForm);
} else {
  initRegistrationForm();
}

// Export for extensibility (if needed by future modules)
if (typeof window !== 'undefined') {
  window.RegistrationFormValidator = {
    validateField,
    validateAllFields,
    showFieldError,
    clearFieldError,
    FIELD_VALIDATORS
  };
}
